import React, { useEffect } from 'react';
import { Redirect, Route as BaseRoute } from 'react-router-dom';
import LoadingIndicator from 'src/components/LoadingIndicator';
import Sidebar from 'src/components/Sidebar';
import TermsModal from 'src/components/TermsModal';
import { useAppState } from 'src/lib/hooks';
import AddressDetails from 'src/pages/AddressDetails';
import NewAddressDetail from 'src/pages/AddressDetails/new';
import AddressDetail from 'src/pages/AddressDetails/view';
import AcceptInvitation from 'src/pages/Auth/accept-invitation';
import ForgotPassword from 'src/pages/Auth/forgot-password';
import Login from 'src/pages/Auth/login';
import ResetPassword from 'src/pages/Auth/reset-password';
import Signup from 'src/pages/Auth/signup';
import TermsOfService from 'src/pages/Auth/terms-of-service';
import VerifySignup from 'src/pages/Auth/verify-signup';
import Dashboard from 'src/pages/Dashboard';
import Guide from 'src/pages/Guide';
import Reports from 'src/pages/Reports';
import Settings from 'src/pages/Settings';
import Billing from 'src/pages/Settings/billing';
import InviteUser from 'src/pages/Settings/invitation';
import Invitations from 'src/pages/Settings/invitations';
import Profile from 'src/pages/Settings/profile';
import EditUser from 'src/pages/Settings/user';
import Users from 'src/pages/Settings/users';
import ShipmentInvoices from 'src/pages/ShipmentInvoices';
import ShipmentInvoicePreview from 'src/pages/ShipmentInvoices/preview';
import ShipmentQuotes from 'src/pages/ShipmentQuotes';
import NewShipmentQuote from 'src/pages/ShipmentQuotes/new';
import ShipmentQuote from 'src/pages/ShipmentQuotes/view';
import Shipments from 'src/pages/Shipments';
import ShipmentPreview from 'src/pages/Shipments/preview';
import Shipment from 'src/pages/Shipments/view';
import Support from 'src/pages/Support';
import { useApp } from 'straightline-ui';
import { parseQuery } from 'straightline-utils/querystring';

// Add query params to the props.location
// which react-router does not support automatically...
export const Route = (props) => {
  props.location.query = parseQuery(props.location.search, {
    ignoreQueryPrefix: true,
    strictNullHandling: true
  });
  return <BaseRoute {...props} />;
};

const ScrollToTopOnMount = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return null;
};

export const PrivateRoute = (props) => {
  // https://stackoverflow.com/questions/9847580/how-to-detect-safari-chrome-ie-firefox-and-opera-browser
  // Even though we do polyfill via `react-app-polyfill`, there
  // are just still kinks with IE.
  const isIE = /*@cc_on!@*/ false || !!document.documentMode;

  const app = useApp();
  const {
    state: { authenticated, prepped }
  } = useAppState();

  const { location } = props;
  const { component: Component, ...rest } = props;

  useEffect(() => {
    const redirect =
      location.pathname === '/' || location.pathname === '/not-found'
        ? null
        : location.pathname;

    if (!authenticated) {
      app.authenticate().catch(() => app.cleanup(redirect));
    }

    if (authenticated && !prepped) {
      app.prep().catch(() => app.cleanup(redirect));
    }
  }, [authenticated, prepped, location, app]);

  if (!authenticated || !prepped) {
    return (
      <div className="min-h-100vh d-flex align-items-center">
        <LoadingIndicator delay={500} />
      </div>
    );
  }

  return (
    <Route
      {...rest}
      render={(props) => {
        return (
          <React.Fragment>
            <ScrollToTopOnMount />
            <Sidebar />
            <div className="main-content">
              {isIE && (
                <div className="p-3 bg-light">
                  Your browser is not supported. Some features may not work or
                  be available.
                  <a
                    href="https://browsehappy.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-2"
                  >
                    Please Upgrade
                  </a>
                </div>
              )}
              <Component {...props} />
            </div>
            <TermsModal />
          </React.Fragment>
        );
      }}
    />
  );
};

export const privateRoutes = [
  {
    path: '/',
    component: () => <Redirect to="/dashboard" />
  },
  {
    path: '/dashboard',
    component: Dashboard,
    key: 'Dashboard'
  },
  {
    path: '/guide',
    component: Guide,
    key: 'Guide'
  },
  {
    path: '/settings',
    component: Settings,
    key: 'Settings'
  },
  {
    path: '/settings/profile',
    component: Profile,
    key: 'Profile'
  },
  {
    path: '/settings/billing',
    component: Billing,
    key: 'Billing'
  },
  {
    path: '/settings/users',
    component: Users,
    key: 'Users'
  },
  {
    path: '/settings/user',
    component: InviteUser,
    key: 'InviteUser'
  },
  {
    path: '/settings/invitations',
    component: Invitations,
    key: 'Invitations'
  },
  {
    path: '/settings/users/:id',
    component: EditUser,
    key: 'EditUser'
  },
  {
    path: '/support',
    component: Support,
    key: 'Support'
  },
  {
    path: '/shipments',
    component: Shipments,
    key: 'Shipments'
  },
  {
    path: '/reports',
    component: Reports,
    key: 'Reports'
  },
  {
    path: '/shipments/:id',
    component: Shipment,
    key: 'Shipment'
  },
  {
    path: '/invoices',
    component: ShipmentInvoices,
    key: 'ShipmentInvoices'
  },
  {
    path: '/quotes',
    component: ShipmentQuotes,
    key: 'ShipmentQuotes'
  },
  {
    path: '/quote',
    component: NewShipmentQuote,
    key: 'NewShipmentQuote'
  },
  {
    path: '/quotes/:id',
    component: ShipmentQuote,
    key: 'ShipmentQuote'
  },
  {
    path: '/addresses',
    component: AddressDetails,
    key: 'AddressDetails'
  },
  {
    path: '/address',
    component: NewAddressDetail,
    key: 'NewAddressDetail'
  },
  {
    path: '/addresses/:id',
    component: AddressDetail,
    key: 'AddressDetail'
  }
];

export const publicRoutes = [
  {
    path: '/login',
    component: Login,
    key: 'login'
  },
  {
    path: '/forgot-password',
    component: ForgotPassword,
    key: 'ForgotPassword'
  },
  {
    path: '/reset-password',
    component: ResetPassword,
    key: 'ResetPassword'
  },
  {
    path: '/signup',
    component: Signup,
    key: 'Signup'
  },
  {
    path: '/verify-signup',
    component: VerifySignup,
    key: 'VerifySignup'
  },
  {
    path: '/accept-invitation/:token',
    component: AcceptInvitation,
    key: 'AcceptInvitation'
  },
  {
    path: '/terms-of-service',
    component: TermsOfService,
    key: 'TermsOfService'
  },
  {
    path: '/shipment-preview',
    component: ShipmentPreview,
    key: 'ShipmentPreview'
  },
  {
    path: '/shipment-invoice-preview',
    component: ShipmentInvoicePreview,
    key: 'ShipmentInvoicePreview'
  }
];
